import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Trash2, Mail, Phone, Globe, Calendar, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cs_admin_token";

const LEAD_TYPES = {
  principal: { label: "Private client", tone: "bg-[#C7A26C] text-[#070E13]", priority: 1 },
  "family-office": { label: "Family office", tone: "bg-[#2FB7B1] text-[#070E13]", priority: 1 },
  hospitality: { label: "Hospitality group", tone: "bg-[#E8DFC8] text-[#070E13]", priority: 2 },
  other: { label: "Other", tone: "bg-[#C77D66] text-white", priority: 3 },
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "principal", label: "Principals" },
  { value: "family-office", label: "Family offices" },
  { value: "hospitality", label: "Hospitality" },
  { value: "other", label: "Other" },
  { value: "untagged", label: "Untagged" },
];

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const counts = useMemo(() => {
    const out = { all: contacts.length, untagged: 0 };
    Object.keys(LEAD_TYPES).forEach((k) => (out[k] = 0));
    contacts.forEach((c) => {
      if (!c.lead_type) out.untagged += 1;
      else if (out[c.lead_type] !== undefined) out[c.lead_type] += 1;
    });
    return out;
  }, [contacts]);

  const filtered = useMemo(() => {
    let list = contacts;
    if (filter === "untagged") list = contacts.filter((c) => !c.lead_type);
    else if (filter !== "all") list = contacts.filter((c) => c.lead_type === filter);
    // High-priority lead types first within filter
    return [...list].sort((a, b) => {
      const pa = LEAD_TYPES[a.lead_type]?.priority ?? 9;
      const pb = LEAD_TYPES[b.lead_type]?.priority ?? 9;
      if (pa !== pb) return pa - pb;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [contacts, filter]);

  useEffect(() => {
    if (token) loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, r.data.access_token);
      setToken(r.data.access_token);
      setPassword("");
      toast.success("Welcome");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setContacts([]);
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load contacts");
      }
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#070E13] text-[#F4EEE8] flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md bg-[#14222C] border-s-2 border-[#2FB7B1] p-10">
          <div className="flex items-center gap-3 mb-8">
            <Lock className="text-[#C7A26C]" size={20} />
            <p className="overline text-[#C7A26C]">Concierge Admin</p>
          </div>
          <h1 className="font-serif text-3xl mb-8">Sign in</h1>
          <label className="overline text-[#F4EEE8]/60 block mb-3">Password</label>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="w-full bg-transparent border-b border-[#F4EEE8]/30 focus:border-[#C7A26C] outline-none py-3 text-[#F4EEE8]"
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="mt-10 w-full bg-[#C7A26C] text-[#070E13] py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#E8DFC8] transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Enter"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 w-full text-xs text-[#F4EEE8]/50 tracking-widest uppercase hover:text-[#C7A26C]"
          >
            ← Back to site
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDE3D0] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between pb-8 border-b border-[#070E13]/15">
          <div>
            <p className="overline text-[#C7A26C]">Concierge admin</p>
            <h1 className="font-serif text-4xl mt-2 text-[#070E13]">Inquiries</h1>
            <p className="text-sm text-[#070E13]/60 mt-2">{contacts.length} total · showing {filtered.length}</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/email-preview"
              data-testid="admin-email-preview-link"
              className="text-xs uppercase tracking-[0.2em] px-4 py-2 border border-[#070E13]/30 hover:border-[#070E13] hidden sm:inline-flex items-center"
            >
              Templates
            </Link>
            <button
              data-testid="admin-refresh"
              onClick={loadContacts}
              className="text-xs uppercase tracking-[0.2em] px-4 py-2 border border-[#070E13]/30 hover:border-[#070E13]"
            >
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="text-xs uppercase tracking-[0.2em] px-4 py-2 bg-[#070E13] text-[#F4EEE8] hover:bg-[#14222C] flex items-center gap-2"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </header>

        {/* FILTER CHIPS */}
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                data-testid={`admin-filter-${f.value}`}
                onClick={() => setFilter(f.value)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  active
                    ? "bg-[#070E13] text-[#F4EEE8] border-[#070E13]"
                    : "bg-transparent text-[#070E13]/70 border-[#070E13]/25 hover:border-[#070E13]"
                }`}
              >
                {f.label} <span className="ms-2 text-[10px] opacity-60">{counts[f.value] ?? 0}</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
          {filtered.length === 0 && !loading && (
            <div className="col-span-full bg-[#070E13] text-[#F4EEE8] p-10 text-center border-s-2 border-[#2FB7B1]">
              <p className="font-serif text-xl">No inquiries in this segment.</p>
            </div>
          )}
          {filtered.map((c) => {
            const tier = c.lead_type ? LEAD_TYPES[c.lead_type] : null;
            const highPriority = tier?.priority === 1;
            return (
              <article
                key={c.id}
                data-testid={`admin-contact-${c.id}`}
                className={`relative bg-[#070E13] text-[#F4EEE8] p-8 border-s-2 ${highPriority ? "border-[#C7A26C]" : "border-[#2FB7B1]"}`}
              >
                <span className="absolute top-0 right-0 w-16 h-px bg-[#C77D66]" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {tier && (
                        <span data-testid={`admin-leadtype-${c.lead_type}`} className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 ${tier.tone}`}>
                          {highPriority && <Star size={10} className="inline -mt-0.5 me-1" />}
                          {tier.label}
                        </span>
                      )}
                      {!tier && (
                        <span className="text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 bg-white/10 text-[#F4EEE8]/60">Untagged</span>
                      )}
                    </div>
                    <h2 className="font-serif text-2xl mt-3">{c.name}</h2>
                    <p className="text-xs text-[#C7A26C] tracking-widest uppercase mt-2">
                      {c.interest || "general"}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`admin-delete-${c.id}`}
                    className="text-[#C77D66]/70 hover:text-[#C77D66] p-2"
                    aria-label="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-[#F4EEE8]/80">
                  <li className="flex gap-3 items-center"><Mail size={14} className="text-[#C7A26C]" /> <a href={`mailto:${c.email}`} className="hover:text-[#C7A26C]">{c.email}</a></li>
                  {c.phone && <li className="flex gap-3 items-center"><Phone size={14} className="text-[#C7A26C]" /> {c.phone}</li>}
                  <li className="flex gap-3 items-center"><Globe size={14} className="text-[#C7A26C]" /> {c.locale?.toUpperCase()}</li>
                  <li className="flex gap-3 items-center"><Calendar size={14} className="text-[#C7A26C]" /> {new Date(c.created_at).toLocaleString()}</li>
                  <li className="flex gap-3 items-center text-xs">
                    <span className={`inline-block w-2 h-2 rounded-full ${c.email_sent ? "bg-[#2FB7B1]" : "bg-[#C77D66]"}`} />
                    Notify {c.email_sent ? "delivered" : "skipped"}
                  </li>
                  <li className="flex gap-3 items-center text-xs">
                    <span className={`inline-block w-2 h-2 rounded-full ${c.auto_reply_sent ? "bg-[#C7A26C]" : "bg-white/15"}`} />
                    Auto-reply {c.auto_reply_sent ? "sent" : "skipped"}
                  </li>
                </ul>
                <p className="mt-6 pt-5 border-t border-white/10 text-[#F4EEE8]/85 leading-relaxed whitespace-pre-wrap">{c.message}</p>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
